import {useEffect, useState} from 'react';
import {View, ActivityIndicator, Text, StyleSheet} from 'react-native';
import {useLocalSearchParams, router} from 'expo-router';
import {useTranslation} from 'react-i18next';
import {getWebToken} from '@/data/app-data-api';
import {useCurrentTheme} from '@/theming/useCurrentTheme';
import {ErrorMessage} from '@/components/common/ErrorMessage';
import {ThemeType} from '@/theming/baseTheme';
import {useAuth} from '@/auth/useAuth';

export default function TokenLogin() {
    const {webTokenId, orgId} = useLocalSearchParams<{webTokenId: string; orgId?: string}>();
    const {t} = useTranslation();
    const {theme} = useCurrentTheme();
    const {loginWithToken} = useAuth();
    const [error, setError] = useState<string | null>(null);

    const styles = createStyles(theme);

    useEffect(() => {
        const authenticateWithToken = async () => {
            if (!webTokenId) {
                setError(t('auth.tokenLogin.missingToken', {defaultValue: 'Invalid login link'}));
                return;
            }

            try {
                // Get the auth token from the backend
                const authToken = await getWebToken(webTokenId);

                // Login with the token using AuthProvider
                await loginWithToken(authToken);

                // Redirect to organization page if orgId is provided, otherwise to main page
                if (orgId) {
                    router.replace(`/organization/${orgId}`);
                } else {
                    router.replace('/');
                }
            } catch (err) {
                console.error('Token login error:', err);
                setError(
                    t('auth.tokenLogin.error', {
                        defaultValue: 'Failed to log in. The link may be invalid or expired.',
                    })
                );
            }
        };

        void authenticateWithToken();
    }, [webTokenId, orgId, loginWithToken, t]);

    return (
        <View style={styles.container}>
            {error ? (
                <View style={styles.errorContainer}>
                    <ErrorMessage message={error} />
                </View>
            ) : (
                <>
                    <ActivityIndicator size="large" color={theme.colors.primary} />
                    <Text style={styles.loadingText}>
                        {t('auth.tokenLogin.loggingIn', {defaultValue: 'Logging you in...'})}
                    </Text>
                </>
            )}
        </View>
    );
}

const createStyles = (theme: ThemeType) =>
    StyleSheet.create({
        container: {
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: theme.colors.background,
            padding: theme.spacing.lg,
        },
        loadingText: {
            marginTop: theme.spacing.lg,
            fontSize: theme.fontSize.lg,
            color: theme.colors.text,
            textAlign: 'center',
        },
        errorContainer: {
            width: '100%',
            maxWidth: 400,
        },
    });
